// src/shared/remeasure.js — „Смени мярката" и „Материали в бройки"
// (Макс, 27.10.2026: материалите на ресторанта да се водят в грамаж).
//
// Сметката е на сървъра (services/remeasure.js): тук е само въпросът „1 бр. =
// колко кг" и прегледът какво ще стане, преди да се запише.

import { el, clear, field, input, row } from './dom.js';
import { toast } from './toast.js';
import { api } from './api.js';
import { openModal, closeModal } from './modal.js';
import { state, cq } from './state.js';
import { quantity } from './format.js';

const num = (v) => { const n = parseFloat(String(v == null ? '' : v).replace(',', '.')); return Number.isFinite(n) ? n : 0; };
const comma = (v) => String(v).replace('.', ',');

// a — артикулът; suggestion — { measure, factor } от името (по избор);
// onDone(article) — след успешна смяна (диалогът вече е затворен).
export function remeasureDialog(a, suggestion, onDone) {
  const old = a.measure || 'бр.';
  const sug = suggestion || {};
  const measure = el('select', {}, ...['кг', 'л', 'г', 'мл'].map((u) => el('option', { value: u, text: u, selected: (sug.measure || 'кг') === u })));
  const factor = input({ type: 'text', inputmode: 'decimal', value: sug.factor ? comma(sug.factor) : '', placeholder: '0,5' });
  const same = el('input', { type: 'checkbox' });
  const preview = el('div', { class: 'remeasure-preview', role: 'status', 'aria-live': 'polite' });
  const okBtn = el('button', { class: 'btn', type: 'button', text: 'Смени мярката', disabled: 'disabled' });
  let seq = 0;
  let last = null;

  const body = () => ({ company_id: state().activeCompany, measure: measure.value, factor: num(factor.value), keep_unit: !!same.checked });
  async function check() {
    factorField.firstChild.textContent = '1 ' + old + ' = колко ' + measure.value;
    const my = ++seq;
    clear(preview);
    okBtn.disabled = true;
    if (!(num(factor.value) > 0)) { preview.appendChild(el('p', { class: 'cal-hint', text: 'Напишете колко ' + measure.value + ' е 1 ' + old + '.' })); return; }
    let p;
    try { p = await api('/articles/' + a.id + '/remeasure', { method: 'POST', body: JSON.stringify(Object.assign(body(), { preview: true })) }); }
    catch (e) { if (my === seq) preview.appendChild(el('p', { class: 'sev-warn', text: e.message })); return; }
    if (my !== seq) return;
    last = p;
    preview.appendChild(el('p', {}, el('strong', { text: 'Наличност: ' + quantity(p.qty_before) + ' ' + old + ' → ' + quantity(p.qty_after) + ' ' + p.measure }),
      ' (стойността остава ' + comma((p.value || 0).toFixed(2)) + ' €).'));
    const what = [];
    if (p.movements) what.push(p.movements + ' движения в склада се преписват (сторно + ново)');
    if (p.purchases) what.push(p.purchases + ' доставки получават разфасовка 1 ' + old + ' = ' + comma(p.factor) + ' ' + p.measure);
    if (p.recipes) what.push(p.recipes + ' рецептури минават в ' + (p.measure === 'л' ? 'мл' : p.measure === 'кг' ? 'г' : p.measure));
    if (what.length) preview.appendChild(el('ul', { style: 'margin:4px 0;padding-left:20px' }, ...what.map((t) => el('li', { text: t }))));
    (p.blockers || []).forEach((t) => preview.appendChild(el('p', { class: 'sev-warn', role: 'alert', text: t })));
    okBtn.disabled = !!(p.blockers || []).length;
  }
  [factor].forEach((x) => x.addEventListener('input', check));
  [measure, same].forEach((x) => x.addEventListener('change', check));

  okBtn.addEventListener('click', async () => {
    if (!last || (last.blockers || []).length) return;
    okBtn.disabled = true;
    try {
      const r = await api('/articles/' + a.id + '/remeasure', { method: 'POST', body: JSON.stringify(body()) });
      toast('„' + a.name + '" вече се води в ' + r.measure + ': ' + quantity(r.qty_after) + ' ' + r.measure + '.');
      closeModal();
      if (onDone) onDone(r.article);
    } catch (e) { toast(e.message, true); okBtn.disabled = false; }
  });

  const factorField = field('1 ' + old + ' = колко ' + measure.value, factor, { size: 's' });
  const content = el('div', {},
    el('p', { text: '„' + (a.name || '') + '" сега се води в „' + old + '". Новата мярка е тази, в която ще се брои складът и ще се пишат рецептите (в грамове/милилитри).' }),
    row(field('Нова мярка', measure, { size: 's' }), factorField),
    el('label', { style: 'display:inline-flex;gap:6px;align-items:center;cursor:pointer;margin:4px 0 8px' }, same,
      'Всички бройки са еднакви (1 ' + old + ' винаги е толкова)'),
    el('p', { class: 'cal-hint', text: 'Не отбелязвайте, ако стоката идва в различни разфасовки (кисело мляко 400 г или 2 кг) — тогава разфасовката се пише на всеки ред от доставката и се попълва сама от описанието.' }),
    preview);
  const foot = el('div', {}, el('button', { class: 'btn ghost', type: 'button', text: 'Отказ', onClick: closeModal }), ' ', okBtn);
  openModal({ title: 'Смени мярката', body: content, foot });
  check();
}

// „Материали в бройки": материалите извън кг/л — един по един „Смени".
export async function packCheckDialog(onChanged) {
  let rows;
  try { rows = await api('/articles/pack-check' + cq()); } catch (e) { toast(e.message, true); return; }
  const tb = el('tbody');
  const draw = () => {
    clear(tb);
    if (!rows.length) { tb.appendChild(el('tr', {}, el('td', { colspan: '4', text: 'Всички материали се водят в кг или л.' }))); return; }
    rows.forEach((r) => {
      const sug = r.suggestion ? '1 ' + r.measure + ' = ' + comma(r.suggestion.factor) + ' ' + r.suggestion.measure : '';
      tb.appendChild(el('tr', {},
        el('td', {}, el('div', { text: r.name }), el('div', { class: 'cal-hint', text: 'код ' + (r.code || '') })),
        el('td', { class: 'num', text: quantity(r.qty) + ' ' + r.measure }),
        el('td', { text: sug }),
        el('td', {}, el('button', { class: 'btn sm', type: 'button', text: 'Смени мярката', 'aria-label': 'Смени мярката на ' + r.name,
          onClick: () => remeasureDialog(r, r.suggestion, () => { rows = rows.filter((x) => x.id !== r.id); draw(); if (onChanged) onChanged(); }) }))));
    });
  };
  draw();
  const body = el('div', {},
    el('p', { text: 'Материалите, които сега се водят в бройки или пакети. За рецептите е по-удобно да са в кг или л — тогава рецептата пише грамове, а доставката в бройки се превръща сама по разфасовката.' }),
    el('p', { class: 'cal-hint', text: 'Яйцата, кюфтетата, палачинките и подобните, които се броят, оставете в бройки.' }),
    el('table', { class: 'list' },
      el('thead', {}, el('tr', {}, el('th', { text: 'Материал' }), el('th', { class: 'num', text: 'Наличност' }), el('th', { text: 'Предложение от името' }), el('th', {}))),
      tb));
  openModal({ title: 'Материали в бройки', body, foot: el('div', {}, el('button', { class: 'btn', type: 'button', text: 'Готово', onClick: closeModal })), wide: true });
}
